import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconUserCircle } from '@tabler/icons-react';

import api from '@helpers/api';
import { profilePath } from '@helpers/profile';
import { postStore } from '@helpers/posts';
import { useAuth } from '@contexts/AuthContext';
import { useChat } from '@contexts/ChatContext';
import { useCommunity } from 'contexts/community';
import { useScreen } from 'Screen/ScreenContext';
import { UserAvatar } from '@components/Community';

// The api helper resolves with the axios *error* on failure instead of throwing,
// so a successful call is one that carries a 2xx `status` and a body.
function okData(response) {
    if (!response || response.isAxiosError) return null;
    if (typeof response.status === 'number' && (response.status < 200 || response.status >= 300)) return null;
    return response.data ?? null;
}

function failureStatus(response) {
    return response?.response?.status ?? response?.status ?? null;
}

// `GET /profile` gives `name` (the display name — legacy `username`) and
// `nickname` (the @handle). Everything downstream wants the app-wide user
// shape, so normalise once here.
function toUser(profile, viewer) {
    if (!profile) return null;
    const isViewer = viewer?.id != null && String(viewer.id) === String(profile.id);
    return {
        id: profile.id,
        username: profile.name || profile.nickname || '',
        nickname: profile.nickname || '',
        avatar: profile.avatar || null,
        verified: isViewer ? !!viewer.verified : false,
    };
}

// `handle` — the nickname (or hashed id fallback) from `/@handle`, passed down
// by SlugRoute; absent on `/profile`.
export function useProfile(handle) {
    const { t } = useTranslation('member');

    const { user: viewer, handleOpenProfileConfigModal } = useAuth();
    const { openChat } = useChat();
    const { community } = useCommunity();
    const { setBreadcrumb } = useScreen();

    const [profile, setProfile] = useState(null);
    // loading | ready | notFound | error
    const [status, setStatus] = useState('loading');
    const [activePost, setActivePost] = useState(null);
    const [shareOpen, setShareOpen] = useState(false);

    const load = useCallback(async () => {
        setStatus('loading');
        setActivePost(null);
        setShareOpen(false);

        const response = await api.get('/profile', handle ? { handle } : {});
        const data = okData(response);

        if (!data || !data.id) {
            setProfile(null);
            setStatus(failureStatus(response) === 404 ? 'notFound' : 'error');
            return;
        }

        setProfile(data);
        setStatus('ready');
    }, [handle]);

    useEffect(() => {
        load();
    }, [load]);

    // `/profile` is always me; `/@handle` is me when the handle is my nickname
    // or hashed id, or once the payload's id matches mine.
    const isOwn = useMemo(() => {
        if (!handle) return true;
        if (!viewer) return false;
        const h = String(handle).toLowerCase();
        if (viewer.nickname && h === String(viewer.nickname).toLowerCase()) return true;
        if (viewer.id != null && h === String(viewer.id).toLowerCase()) return true;
        return !!profile?.id && String(profile.id) === String(viewer.id);
    }, [handle, viewer, profile?.id]);

    // The profile settings modal edits the signed-in user in place — mirror
    // those edits so the header doesn't lag behind the sidebar.
    const viewerUsername = viewer?.username;
    const viewerAvatar = viewer?.avatar;
    const viewerNickname = viewer?.nickname;
    useEffect(() => {
        if (!isOwn || !viewer) return;
        setProfile(p => (p ? {
            ...p,
            name: viewerUsername ?? p.name,
            avatar: viewerAvatar ?? p.avatar,
            nickname: viewerNickname ?? p.nickname,
        } : p));
    }, [isOwn, viewerUsername, viewerAvatar, viewerNickname]);

    const user = useMemo(() => toUser(profile, viewer), [profile, viewer]);
    const communities = profile?.communities ?? [];
    const posts = Array.isArray(profile?.posts) ? profile.posts : [];
    const postsTotal = profile?.posts_total ?? posts.length;

    useEffect(() => {
        document.title = user?.username
            ? t('publicProfile.documentTitle', { name: user.username })
            : t('publicProfile.documentTitleFallback');
    }, [user?.username, t]);

    useEffect(() => {
        setBreadcrumb([
            { label: t('publicProfile.breadcrumb'), icon: <IconUserCircle size={18} /> },
            {
                label: user?.username || t('common:actions.loading'),
                icon: user ? <UserAvatar user={user} size={24} link={false} /> : null,
            },
        ]);
        return () => setBreadcrumb(null);
    }, [user, t, setBreadcrumb]);

    const onEditProfile = useCallback(() => {
        handleOpenProfileConfigModal();
    }, [handleOpenProfileConfigModal]);

    const onSendMessage = useCallback(() => {
        if (!user?.id) return;
        openChat({ user_id: user.id, username: user.username, avatar: user.avatar });
    }, [openChat, user]);

    const onOpenCommunity = useCallback((c) => {
        if (c?.slug) community.open(c.slug);
    }, [community]);

    // The grid's copy goes into the shared post store so the modal opens on
    // it at once and refreshes from the API behind it.
    const onOpenPost = useCallback((post) => {
        if (post?.id) postStore.set(post);
        setActivePost(post);
    }, []);
    const onClosePost = useCallback(() => setActivePost(null), []);
    const onGoHome = useCallback(() => window.navigate('/'), []);

    // Public link for the share card — nickname form, never `/profile`.
    const profileUrl = user?.id ? `${window.location.origin}${profilePath(user)}` : '';
    const onOpenShare = useCallback(() => setShareOpen(true), []);
    const onCloseShare = useCallback(() => setShareOpen(false), []);

    return {
        status,
        isOwn,
        user,
        description: profile?.description || '',
        postsTotal,
        communities,
        posts,
        activePost,
        profileUrl,
        shareOpen,

        onRetry: load,
        onGoHome,
        onEditProfile,
        onSendMessage,
        onOpenCommunity,
        onOpenPost,
        onClosePost,
        onOpenShare,
        onCloseShare,
    };
}
